import type { DealCard } from '@/app/components/search/utils/buildDealCards/dealCardTypes.ts';
import type { Localization } from '@/app/components/search/common/Localization.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import {
  type Feature,
  FeatureType,
  type TooltipRenderProps,
} from '@/app/components/search/components/EvolutionCard/utils/facilitiesTypes.ts';
import { formatPrice } from '@/app/components/search/utils/formatting.ts';
import dynamic from 'next/dynamic';
import type { ModalProps } from '@/app/components/search/components/EvolutionCard/Details/Features/getFeatures/types.ts';
import React from 'react';
import type { TransportData } from '@/domainLogic/index.ts';
import TsxTooltip from '@/app/components/search/components/Tooltip/TsxTooltip';
import { getGroupLuggageTooltipText } from '@/app/components/search/utils/groupLuggageTooltip.ts';
import type { StarfishIconRef } from '@/app/common/icons/starfish';

export const GroupLuggageModal = dynamic(() => import('../../Modal/Modals/GroupLuggageModal'), {
  ssr: false,
});

const getSmallBagsCountText = ({ adultsChildrenCount, t }: { adultsChildrenCount: number; t: TranslationFunction }) =>
  adultsChildrenCount === 1
    ? t('vacation.select.cc.luggage.hand.single')
    : t('vacation.select.cc.luggage.hand.plural', { num: adultsChildrenCount.toString() });

const getCabinBagsCountText = ({
  transportData,
  adultsChildrenCount,
  t,
}: {
  transportData?: TransportData;
  adultsChildrenCount: number;
  t: TranslationFunction;
}) => {
  const hasHandBaggageIncluded = transportData?.inbound?.baggage?.hand;

  return hasHandBaggageIncluded
    ? adultsChildrenCount === 1
      ? t('vacation.select.cc.luggage.cabin.single')
      : t('vacation.select.cc.luggage.cabin.plural', { num: adultsChildrenCount.toString() })
    : null;
};

const getCheckedBagsCountText = ({
  hasGroupLuggageFeature,
  numberOfGroupLuggagePieces,
  transportData,
  adultsChildrenCount,
  t,
  localization,
  showFallback,
}: {
  hasGroupLuggageFeature: boolean;
  numberOfGroupLuggagePieces: number;
  transportData?: TransportData;
  adultsChildrenCount: number;
  t: TranslationFunction;
  localization: Localization;
  showFallback: boolean;
}) => {
  const hasCheckedBaggageIncluded = transportData?.inbound?.baggage?.hold;

  if (hasGroupLuggageFeature) {
    const isSinglePiece = numberOfGroupLuggagePieces === 1 || (hasCheckedBaggageIncluded && adultsChildrenCount === 1);
    const isMultiplePieces = numberOfGroupLuggagePieces > 1 || (hasCheckedBaggageIncluded && adultsChildrenCount > 1);

    if (isSinglePiece) {
      return t('vacation.select.cc.luggage.checked.total.single');
    }

    if (isMultiplePieces) {
      const pieces = numberOfGroupLuggagePieces > 1 ? numberOfGroupLuggagePieces : adultsChildrenCount;
      return t('vacation.select.cc.luggage.checked.total.plural', { num: pieces.toString() });
    }
  }

  if (hasCheckedBaggageIncluded) {
    if (adultsChildrenCount === 1) {
      return t('vacation.select.cc.luggage.checked.single');
    }

    if (adultsChildrenCount > 1) {
      return t('vacation.select.cc.luggage.checked.plural', { num: adultsChildrenCount.toString() });
    }
  }

  const priceFrom = transportData?.baggagePriceFrom;

  if (showFallback) {
    if (priceFrom) {
      const price = formatPrice({
        price: priceFrom,
        localization,
      });

      return t('vacation.oss.cards.flights.luggage_checked_from', { amount: `${price}` });
    }

    return t('vacation.oss.cards.flights.luggage_later');
  }

  return null;
};

const getCombinedBagsText = (
  smallBagsCountText?: string | null,
  cabinBagsCountText?: string | null,
  checkedBagsCountText?: string | null
): string | undefined => {
  if (!cabinBagsCountText && !checkedBagsCountText) {
    return smallBagsCountText || undefined;
  }
  const parts: string[] = [];
  if (cabinBagsCountText) parts.push(cabinBagsCountText);
  if (checkedBagsCountText) parts.push(checkedBagsCountText);
  return parts.length > 0 ? parts.join(', ') : undefined;
};

const getBaggageIcon = (transportData?: TransportData): StarfishIconRef => {
  const hasHandBaggageIncluded = transportData?.inbound?.baggage?.hand;
  const hasCheckedBaggageIncluded = transportData?.inbound?.baggage?.hold;
  const hasGroupLuggage = (transportData?.includedTotalCheckedBags ?? 0) > 0;
  const hasMultipleBags = Boolean(hasHandBaggageIncluded && (hasCheckedBaggageIncluded || hasGroupLuggage));

  if (hasMultipleBags) {
    return { category: 'Luggage', name: 'icon-luggage', color: 'iconPrimary' };
  }

  if (hasCheckedBaggageIncluded) {
    return { category: 'Luggage', name: 'icon-suitcase', color: 'iconPrimary' };
  }

  if (hasHandBaggageIncluded) {
    return { category: 'Luggage', name: 'icon-carry-on', color: 'iconPrimary' };
  }

  return { category: 'Luggage', name: 'icon-backpack', color: 'iconPrimary' };
};

export const getBaggage = ({
  dealCard,
  transportData,
  t,
  localization,
  adultsChildrenCount,
  isMobile,
  showFallback = true,
  isOpx,
}: {
  dealCard: DealCard;
  transportData?: TransportData;
  t: TranslationFunction;
  localization: Localization;
  adultsChildrenCount: number;
  isMobile: boolean;
  showFallback: boolean;
  isOpx: boolean;
}): Feature | undefined => {
  if (isOpx) return undefined;
  const numberOfGroupLuggagePieces = transportData?.includedTotalCheckedBags ?? 0;
  const hasGroupLuggageFeature = numberOfGroupLuggagePieces > 0;
  const showGroupLuggageTooltip = adultsChildrenCount > 1 && numberOfGroupLuggagePieces > 0 && !isMobile;
  const showGroupLuggageModal = adultsChildrenCount > 1 && numberOfGroupLuggagePieces > 0 && isMobile;
  const smallBagsCountText = getSmallBagsCountText({ adultsChildrenCount, t });
  const cabinBagsCountText = getCabinBagsCountText({
    transportData,
    adultsChildrenCount,
    t,
  });
  const checkedBagsCountText = getCheckedBagsCountText({
    hasGroupLuggageFeature,
    numberOfGroupLuggagePieces,
    transportData,
    adultsChildrenCount,
    t,
    localization,
    showFallback,
  });

  const combinedBagsText = getCombinedBagsText(smallBagsCountText, cabinBagsCountText, checkedBagsCountText);
  const combinedIcon = getBaggageIcon(transportData);

  return {
    icon: combinedIcon,
    text: combinedBagsText,
    featureType: FeatureType.normal,
    ...(showGroupLuggageModal
      ? {
          Modal: ({ isOpen, onClose }: ModalProps) =>
            React.createElement(GroupLuggageModal, { isOpen, onClose, dealCard }),
        }
      : showGroupLuggageTooltip
        ? {
            Tooltip: (props: TooltipRenderProps) =>
              React.createElement(TsxTooltip, {
                text: combinedBagsText,
                tooltip: getGroupLuggageTooltipText({
                  variant: 'checked',
                  numberOfGroupLuggagePieces,
                  t,
                }),
                size: 213,
                ...props,
              }),
          }
        : {}),
  };
};
